import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  listFloorPlans,
  createFloorPlan,
  updateFloorPlan,
  deleteFloorPlan,
} from '../utils/api';
import { useToast } from './Toast';

const STATUS_COLORS = {
  vacant: { fill: 'rgba(220, 38, 38, 0.35)', stroke: '#dc2626', labelAr: 'شاغر', labelEn: 'Vacant' },
  reserved: { fill: 'rgba(202, 138, 4, 0.4)', stroke: '#ca8a04', labelAr: 'محجوز', labelEn: 'Reserved' },
  rented: { fill: 'rgba(22, 163, 74, 0.35)', stroke: '#16a34a', labelAr: 'مؤجر', labelEn: 'Rented' },
  maintenance: { fill: 'rgba(100, 116, 139, 0.4)', stroke: '#64748b', labelAr: 'صيانة', labelEn: 'Maintenance' },
};

const SECTION_NONE = '__none__';

function unitLocationLabel(u, language) {
  if (!u) return '';
  const floor =
    u.floor_number != null && u.floor_number !== ''
      ? language === 'ar'
        ? `الطابق ${u.floor_number}`
        : `Floor ${u.floor_number}`
      : null;
  const section = u.section
    ? language === 'ar'
      ? `القسم ${u.section}`
      : `Section ${u.section}`
    : null;
  const type =
    u.unit_type === 'villa'
      ? language === 'ar'
        ? 'فيلا / دور'
        : 'Villa / floor'
      : u.unit_type === 'apartment'
        ? language === 'ar'
          ? 'شقة'
          : 'Apt'
        : u.unit_type || '';
  return [type, floor, section, u.unit_number].filter(Boolean).join(' · ');
}

/**
 * Hierarchical blueprint: Building/Villa → Floor/Level → Section → Unit.
 * Riyadh-common: apartments need floor+section; villa floors often sell separately.
 */
const UnitBlueprint = ({
  language = 'ar',
  property,
  units = [],
  onSelectUnit,
  onBack,
}) => {
  const isRTL = language === 'ar';
  const toast = useToast();
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);
  const [mapUnitId, setMapUnitId] = useState('');
  const [floorLabel, setFloorLabel] = useState('');
  const [floorNumber, setFloorNumber] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const imgRef = useRef(null);

  const plan = plans.find((p) => p.id === planId) || plans[0] || null;

  const unitsForProperty = useMemo(
    () => units.filter((u) => String(u.property_id) === String(property?.id)),
    [units, property]
  );

  const isVillaHeavy = useMemo(() => {
    const villas = unitsForProperty.filter((u) => u.unit_type === 'villa').length;
    const apts = unitsForProperty.filter((u) => u.unit_type === 'apartment').length;
    return villas > 0 && villas >= apts;
  }, [unitsForProperty]);

  const activeFloorNum = plan?.floor_number != null ? Number(plan.floor_number) : null;

  const unitsOnFloor = useMemo(() => {
    if (activeFloorNum == null) return unitsForProperty;
    return unitsForProperty.filter(
      (u) => u.floor_number == null || Number(u.floor_number) === activeFloorNum
    );
  }, [unitsForProperty, activeFloorNum]);

  const sectionsOnFloor = useMemo(() => {
    const set = new Set();
    unitsOnFloor.forEach((u) => {
      if (u.section) set.add(String(u.section));
    });
    return [...set].sort();
  }, [unitsOnFloor]);

  const visibleUnits = useMemo(() => {
    if (sectionFilter === 'all') return unitsOnFloor;
    if (sectionFilter === SECTION_NONE) return unitsOnFloor.filter((u) => !u.section);
    return unitsOnFloor.filter((u) => String(u.section) === sectionFilter);
  }, [unitsOnFloor, sectionFilter]);

  const groupedBySection = useMemo(() => {
    const groups = new Map();
    visibleUnits.forEach((u) => {
      const key = u.section || SECTION_NONE;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(u);
    });
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === SECTION_NONE) return 1;
      if (b === SECTION_NONE) return -1;
      return String(a).localeCompare(String(b));
    });
  }, [visibleUnits]);

  const visibleHotspots = useMemo(() => {
    const ids = new Set(visibleUnits.map((u) => Number(u.id)));
    return (plan?.hotspots || []).filter((h) => ids.has(Number(h.unit_id)));
  }, [plan, visibleUnits]);

  const load = async () => {
    if (!property?.id) return;
    setLoading(true);
    try {
      const res = await listFloorPlans(property.id);
      const rows = res.data?.floor_plans || [];
      setPlans(rows);
      if (rows[0] && !planId) setPlanId(rows[0].id);
      else if (rows.length && !rows.find((r) => r.id === planId)) setPlanId(rows[0].id);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [property?.id]);

  useEffect(() => {
    if (plan) {
      setFloorLabel(plan.floor_label || '');
      setFloorNumber(plan.floor_number != null ? String(plan.floor_number) : '');
    }
  }, [plan?.id]);

  useEffect(() => {
    setSectionFilter('all');
  }, [plan?.id]);

  const unitById = (id) => unitsForProperty.find((u) => Number(u.id) === Number(id));

  const onImageClick = (e) => {
    if (!editMode || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setDraftPoints((prev) => [...prev, [Math.round(x * 10) / 10, Math.round(y * 10) / 10]]);
  };

  const saveHotspot = async () => {
    if (!plan || draftPoints.length < 3 || !mapUnitId) {
      toast?.error?.(
        language === 'ar' ? 'اختر وحدة وارسم ٣ نقاط على الأقل' : 'Pick a unit and at least 3 points'
      );
      return;
    }
    const unit = unitById(mapUnitId);
    const next = [
      ...(plan.hotspots || []).filter((h) => Number(h.unit_id) !== Number(mapUnitId)),
      {
        unit_id: Number(mapUnitId),
        label: unitLocationLabel(unit, language) || unit?.unit_number || String(mapUnitId),
        section: unit?.section || null,
        floor_number: unit?.floor_number ?? plan.floor_number ?? null,
        points: draftPoints,
      },
    ];
    try {
      const res = await updateFloorPlan(plan.id, { hotspots: next });
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? res.data : p)));
      setDraftPoints([]);
      setMapUnitId('');
      toast?.success?.(language === 'ar' ? 'تم ربط الوحدة على هذا الطابق' : 'Unit mapped on this floor');
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  };

  const persistFloorMeta = async () => {
    if (!plan) return;
    try {
      const res = await updateFloorPlan(plan.id, {
        floor_label: floorLabel || plan.floor_label,
        floor_number: floorNumber === '' ? null : parseInt(floorNumber, 10),
      });
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? res.data : p)));
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  };

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 1_800_000) {
      toast?.error?.(language === 'ar' ? 'الصورة كبيرة جداً' : 'Image too large');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      try {
        if (plan) {
          const res = await updateFloorPlan(plan.id, { image_url: dataUrl });
          setPlans((prev) => prev.map((p) => (p.id === plan.id ? res.data : p)));
        } else {
          const n = floorNumber === '' ? plans.length : parseInt(floorNumber, 10);
          const res = await createFloorPlan(property.id, {
            floor_label:
              floorLabel ||
              (language === 'ar' ? `الطابق ${n || 0}` : `Floor ${n || 0}`),
            floor_number: Number.isFinite(n) ? n : null,
            image_url: dataUrl,
            hotspots: [],
          });
          setPlans((prev) => [...prev, res.data]);
          setPlanId(res.data.id);
        }
        toast?.success?.(language === 'ar' ? 'تم رفع مخطط الطابق' : 'Floor plan uploaded');
      } catch (e) {
        toast?.error?.(e.response?.data?.error || e.message);
      }
    };
    reader.readAsDataURL(file);
  };

  const addFloor = async () => {
    const nextNum =
      floorNumber !== ''
        ? parseInt(floorNumber, 10)
        : Math.max(-1, ...plans.map((p) => (p.floor_number != null ? Number(p.floor_number) : -1))) + 1;
    try {
      const res = await createFloorPlan(property.id, {
        floor_label:
          floorLabel ||
          (language === 'ar' ? `الطابق ${nextNum}` : `Floor ${nextNum}`),
        floor_number: nextNum,
        image_url: null,
        hotspots: [],
      });
      setPlans((prev) => [...prev, res.data]);
      setPlanId(res.data.id);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  };

  const removePlan = async () => {
    if (!plan) return;
    if (!window.confirm(language === 'ar' ? 'حذف مخطط هذا الطابق؟' : 'Delete this floor plan?')) return;
    try {
      await deleteFloorPlan(plan.id);
      const next = plans.filter((p) => p.id !== plan.id);
      setPlans(next);
      setPlanId(next[0]?.id || null);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  };

  const pointsToSvg = (points) => (points || []).map(([x, y]) => `${x},${y}`).join(' ');

  const breadcrumb = [
    property?.name || (language === 'ar' ? 'العقار' : 'Property'),
    plan
      ? plan.floor_label ||
        (plan.floor_number != null
          ? language === 'ar'
            ? `الطابق ${plan.floor_number}`
            : `Floor ${plan.floor_number}`
          : language === 'ar'
            ? 'طابق'
            : 'Floor')
      : null,
    sectionFilter !== 'all' && sectionFilter !== SECTION_NONE
      ? language === 'ar'
        ? `القسم ${sectionFilter}`
        : `Section ${sectionFilter}`
      : sectionFilter === SECTION_NONE
        ? language === 'ar'
          ? 'بدون قسم'
          : 'No section'
        : null,
  ].filter(Boolean);

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          border: 'none',
          background: 'transparent',
          color: '#ea580c',
          cursor: 'pointer',
          fontWeight: 700,
          padding: 0,
          marginBottom: 8,
        }}
      >
        ← {language === 'ar' ? 'رجوع للوحدات' : 'Back to units'}
      </button>

      <div style={{ marginBottom: 6, fontSize: '0.8rem', color: '#64748b' }}>
        {breadcrumb.map((part, i) => (
          <span key={`${part}-${i}`}>
            {i > 0 && <span style={{ margin: '0 6px', opacity: 0.5 }}>›</span>}
            <span style={{ fontWeight: i === breadcrumb.length - 1 ? 700 : 500, color: i === breadcrumb.length - 1 ? '#0f172a' : undefined }}>
              {part}
            </span>
          </span>
        ))}
      </div>

      <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem' }}>
        {language === 'ar' ? 'مخطط المبنى / الفيلا' : 'Building / villa blueprint'}
      </h2>
      <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: '0.85rem', maxWidth: 640 }}>
        {isVillaHeavy
          ? language === 'ar'
            ? 'وضع شائع في الرياض: كل دور في الفيلا قد يُباع أو يُؤجر وحده — اختر الطابق ثم اربط الوحدة (الدور) على المخطط.'
            : 'Common in Riyadh: each villa floor may sell/rent separately — pick the level, then map that floor-unit on the plan.'
          : language === 'ar'
            ? 'للشقق: اختر الطابق ثم القسم (جناح/بلوك) ثم الوحدة على المخطط.'
            : 'For apartments: pick floor → section (wing/block) → unit on the plan.'}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
          {language === 'ar' ? 'الطوابق' : 'Levels'}
        </span>
        {plans.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPlanId(p.id)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: p.id === plan?.id ? '#ea580c' : '#fff',
              color: p.id === plan?.id ? '#fff' : '#475569',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}
          >
            {p.floor_label}
            {p.floor_number != null ? ` (${p.floor_number})` : ''}
          </button>
        ))}
        <button
          type="button"
          onClick={addFloor}
          style={{
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px dashed #fb923c',
            background: '#fff7ed',
            color: '#c2410c',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '0.8rem',
          }}
        >
          + {language === 'ar' ? 'طابق' : 'Level'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditMode((v) => !v);
            setDraftPoints([]);
          }}
          style={{
            marginInlineStart: 'auto',
            padding: '6px 12px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: editMode ? '#fff7ed' : '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        >
          {editMode
            ? language === 'ar'
              ? 'وضع العرض'
              : 'View'
            : language === 'ar'
              ? 'وضع الربط'
              : 'Map units'}
        </button>
      </div>

      {sectionsOnFloor.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
            {language === 'ar' ? 'القسم / الجناح' : 'Section / wing'}
          </span>
          <button
            type="button"
            onClick={() => setSectionFilter('all')}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: sectionFilter === 'all' ? '#0f172a' : '#fff',
              color: sectionFilter === 'all' ? '#fff' : '#475569',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            {language === 'ar' ? 'الكل' : 'All'}
          </button>
          {sectionsOnFloor.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSectionFilter(s)}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                background: sectionFilter === s ? '#0f172a' : '#fff',
                color: sectionFilter === s ? '#fff' : '#475569',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              {s}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSectionFilter(SECTION_NONE)}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: sectionFilter === SECTION_NONE ? '#0f172a' : '#fff',
              color: sectionFilter === SECTION_NONE ? '#fff' : '#475569',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            {language === 'ar' ? 'بدون قسم' : 'No section'}
          </button>
        </div>
      )}

      {loading && <p style={{ color: '#94a3b8' }}>…</p>}

      {plan && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.35fr) minmax(260px, 0.85fr)',
            gap: 14,
            alignItems: 'start',
          }}
        >
          <div
            style={{
              position: 'relative',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              overflow: 'hidden',
              minHeight: 280,
            }}
          >
            {plan.image_url ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <img
                  ref={imgRef}
                  src={plan.image_url}
                  alt={plan.floor_label}
                  onClick={onImageClick}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    cursor: editMode ? 'crosshair' : 'default',
                    userSelect: 'none',
                  }}
                />
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: editMode ? 'none' : 'auto',
                  }}
                >
                  {visibleHotspots.map((h) => {
                    const u = unitById(h.unit_id);
                    const st = STATUS_COLORS[u?.status] || STATUS_COLORS.vacant;
                    return (
                      <g key={`${h.unit_id}-${h.label}`}>
                        <polygon
                          points={pointsToSvg(h.points)}
                          fill={st.fill}
                          stroke={st.stroke}
                          strokeWidth={0.4}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (u && onSelectUnit) onSelectUnit(u);
                          }}
                        />
                      </g>
                    );
                  })}
                  {draftPoints.length > 0 && (
                    <polyline
                      points={pointsToSvg(draftPoints)}
                      fill="none"
                      stroke="#ea580c"
                      strokeWidth={0.5}
                    />
                  )}
                  {draftPoints.map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r={0.8} fill="#ea580c" />
                  ))}
                </svg>
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                {language === 'ar' ? 'ارفع صورة مخطط هذا الطابق' : 'Upload this level’s floor-plan image'}
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: 'inline-block',
                      padding: '8px 14px',
                      background: '#ea580c',
                      color: '#fff',
                      borderRadius: 8,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    {language === 'ar' ? 'رفع' : 'Upload'}
                    <input type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              padding: 14,
              display: 'grid',
              gap: 10,
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
              <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                {language === 'ar' ? 'اسم الطابق' : 'Level name'}
                <input
                  value={floorLabel}
                  onChange={(e) => setFloorLabel(e.target.value)}
                  onBlur={persistFloorMeta}
                  placeholder={language === 'ar' ? 'الأرضي / الأول…' : 'Ground / 1st…'}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                  }}
                />
              </label>
              <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                #
                <input
                  type="number"
                  value={floorNumber}
                  onChange={(e) => setFloorNumber(e.target.value)}
                  onBlur={persistFloorMeta}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                  }}
                />
              </label>
            </div>

            <label style={{ fontSize: '0.8rem', color: '#475569' }}>
              {language === 'ar' ? 'صورة المخطط' : 'Plan image'}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onFile(e.target.files?.[0])}
                style={{ display: 'block', marginTop: 6, fontSize: '0.8rem' }}
              />
            </label>

            {editMode && (
              <>
                <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                  {language === 'ar' ? 'وحدة هذا الطابق / القسم' : 'Unit on this floor/section'}
                  <select
                    value={mapUnitId}
                    onChange={(e) => setMapUnitId(e.target.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                    }}
                  >
                    <option value="">—</option>
                    {visibleUnits.map((u) => (
                      <option key={u.id} value={u.id}>
                        {unitLocationLabel(u, language)} ({u.status})
                      </option>
                    ))}
                  </select>
                </label>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>
                  {language === 'ar'
                    ? `نقاط: ${draftPoints.length} — انقر على المخطط لرسم حدود الوحدة`
                    : `Points: ${draftPoints.length} — click the plan to outline the unit`}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={saveHotspot}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#ea580c',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'ar' ? 'حفظ الربط' : 'Save mapping'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftPoints([])}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    {language === 'ar' ? 'مسح' : 'Clear'}
                  </button>
                </div>
              </>
            )}

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 8 }}>
                {language === 'ar' ? 'وحدات هذا المستوى' : 'Units on this level'}
              </div>
              {groupedBySection.length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  {language === 'ar'
                    ? 'لا وحدات مربوطة بهذا الطابق — عيّن رقم الطابق والقسم في بطاقة الوحدة.'
                    : 'No units on this floor — set floor # and section on the unit card.'}
                </p>
              )}
              {groupedBySection.map(([sec, list]) => (
                <div key={sec} style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      color: '#ea580c',
                      marginBottom: 4,
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    {sec === SECTION_NONE
                      ? isVillaHeavy
                        ? language === 'ar'
                          ? 'دور كامل (بدون قسم)'
                          : 'Whole floor (no section)'
                        : language === 'ar'
                          ? 'بدون قسم'
                          : 'No section'
                      : language === 'ar'
                        ? `القسم ${sec}`
                        : `Section ${sec}`}
                  </div>
                  {list.map((u) => {
                    const mapped = (plan.hotspots || []).some((h) => Number(h.unit_id) === Number(u.id));
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => onSelectUnit?.(u)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: isRTL ? 'right' : 'left',
                          padding: '8px 0',
                          border: 'none',
                          borderBottom: '1px solid #f8fafc',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                        }}
                      >
                        <strong>{u.unit_number}</strong>
                        <span style={{ color: '#64748b', marginInlineStart: 8 }}>
                          {u.status}
                          {mapped ? (language === 'ar' ? ' · على المخطط' : ' · on plan') : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_COLORS).map(([k, v]) => (
                <span key={k} style={{ color: v.stroke }}>
                  ● {language === 'ar' ? v.labelAr : v.labelEn}
                </span>
              ))}
            </div>

            <button
              type="button"
              onClick={removePlan}
              style={{
                marginTop: 4,
                padding: '8px',
                borderRadius: 8,
                border: '1px solid #fecaca',
                background: '#fff',
                color: '#b91c1c',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {language === 'ar' ? 'حذف مخطط الطابق' : 'Delete this level plan'}
            </button>
          </div>
        </div>
      )}

      {!loading && !plans.length && (
        <div
          style={{
            padding: 28,
            border: '1px dashed #cbd5e1',
            borderRadius: 14,
            textAlign: 'center',
            color: '#64748b',
          }}
        >
          <p style={{ margin: '0 0 12px' }}>
            {language === 'ar'
              ? 'ابدأ بطابق: أنشئ مستوى ثم ارفع مخططه واربط الوحدات حسب القسم.'
              : 'Start with a level: create a floor, upload its plan, map units by section.'}
          </p>
          <button
            type="button"
            onClick={addFloor}
            style={{
              padding: '10px 16px',
              background: '#ea580c',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {language === 'ar' ? 'إنشاء أول طابق' : 'Create first level'}
          </button>
        </div>
      )}
    </div>
  );
};

export default UnitBlueprint;
